// src/lib/opportunity/salesAdjustmentsEval.ts
//
// Rep-entered subtotal adjustments (7c). Fixed lines add directly;
// percent lines apply to the original base amount (not a running sum).

import { round2, safePct } from "./calc.js";
import type { OpportunitySalesAdjustment } from "../../types/opportunity.js";

export type EvaluatedSalesAdjustment = OpportunitySalesAdjustment & {
  amount: number;
};

function signedNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const n = Number(value.replace(/[$,\s]/g, ""));
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

export type SalesAdjustmentsEvaluation = {
  baseAmount: number;
  lines: EvaluatedSalesAdjustment[];
  adjustmentTotal: number;
  adjustedAmount: number;
};

export function evaluateSalesAdjustments(
  baseAmount: number,
  adjustments: OpportunitySalesAdjustment[] | null | undefined,
): SalesAdjustmentsEvaluation {
  const base = round2(signedNumber(baseAmount));
  const lines: EvaluatedSalesAdjustment[] = [];
  let adjustmentTotal = 0;

  for (const adj of adjustments ?? []) {
    if (!adj?.description?.trim()) continue;
    const rawValue = signedNumber(adj.value);
    const amount =
      adj.amount_mode === "percent"
        ? round2(base * (safePct(rawValue) / 100))
        : round2(rawValue); // "amount" or legacy "fixed"
    lines.push({ ...adj, amount });
    adjustmentTotal = round2(adjustmentTotal + amount);
  }

  return {
    baseAmount: base,
    lines,
    adjustmentTotal,
    adjustedAmount: round2(base + adjustmentTotal),
  };
}
